import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { useDispatch, useSelector } from 'src/store';
import { reviewLogin } from 'src/slices/auth';
import { selectProduct } from 'src/slices/product';

const AppUrlListener: React.FC<any> = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  // Kept in a ref so the (mount-only) URL handler always sees the latest loaded products.
  const products = useSelector((state) => state.product.products);
  const productsRef = useRef(products);
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    const handleUrl = (url: string) => {
      try {
        const urlObject = new URL(url);

        if (urlObject.hostname === 'review-login' || urlObject.pathname.match(/\/review-login/gi)) {
          const token = urlObject.searchParams.get('token');
          if (token) {
            dispatch(reviewLogin(token));
            history.replace('/tabs/menu');
          }
          return;
        }

        const productSlugMatch = urlObject.pathname.match(/\/tabs\/menu\/product\/([\w-]+)/i);

        if (urlObject.pathname.match(/\/tabs\/orders\/\d+\/thank-you/gi)) {
          history.replace(`${urlObject.pathname}${urlObject.search}`);
        } else if (productSlugMatch) {
          // If the products are already loaded (app warm), select the product and open details
          // directly. Routing through the /product/:slug screen while a details page is open made
          // the router outlet bounce details→slug→details, which intermittently corrupted it into a
          // blank page. Going straight to details (often an in-place update) avoids that. When the
          // list isn't loaded yet (cold start), fall back to the /product/:slug route which resolves
          // and selects the product itself.
          const found = (productsRef.current || []).find((p) => p.slug === productSlugMatch[1]);
          if (found) {
            dispatch(selectProduct(found.id));
            history.replace('/tabs/menu/details');
          } else {
            history.replace(`${urlObject.pathname}${urlObject.search}`);
          }
        } else if (urlObject.pathname.match(/\/tabs\//gi)) {
          history.replace(`${urlObject.pathname}${urlObject.search}`);
        }
      } catch (error) {
        console.error('Failed to parse event url on appUrlOpen, error: ', error);
      }
    };

    // Cold start: the launch URL is delivered before this listener mounts, so appUrlOpen misses it.
    App.getLaunchUrl().then((launch) => {
      if (launch?.url) handleUrl(launch.url);
    });

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => handleUrl(event.url));
  }, []);

  return null;
};

export default AppUrlListener;
